import React, {useRef} from 'react';
import {useIntl} from 'react-intl';
import styled from 'styled-components';
import {
	ColumnSizingState,
	Row,
	SortingState,
	VisibilityState,
	flexRender,
	functionalUpdate,
	getCoreRowModel,
	getExpandedRowModel,
	getSortedRowModel,
	useReactTable,
} from '@tanstack/react-table';
import {useVirtualizer} from '@tanstack/react-virtual';
import {closestCenter, DndContext, DragEndEvent, KeyboardSensor, PointerSensor, useSensor, useSensors} from '@dnd-kit/core';
import {arrayMove, horizontalListSortingStrategy, SortableContext, sortableKeyboardCoordinates} from '@dnd-kit/sortable';
import {Button, Text, Tooltip, tokensForThemeProvider} from '@forecast-it/design-system';
import {ReportRow} from '../data/reportRowModel';
import {ReportDefinitionV1} from '../contracts/report-definition.types';
import {ReportTaxonomy} from '../contracts/report-taxonomy.types';
import {EXPANDER_COLUMN_ID, FAVORITE_COLUMN_ID, FIXED_COLUMN_IDS, useReportTableColumns} from './useReportTableColumns';
import {DraggableHeader} from './DraggableHeader';
import {GroupHeaderRow, GroupTotal, WithheldReason} from './useClientGrouping';
import {formatDuration} from './durationFormat';
import {useIsTruncated} from './useIsTruncated';

const tokens = tokensForThemeProvider.sky.sys;

export interface ReportTableState {
	sorting: SortingState;
	columnOrder: string[];
	columnSizing: ColumnSizingState;
	columnVisibility: VisibilityState;
}

export interface ReportTableProps {
	rows: ReportRow[]; // already grouped/pre-transformed (Task 4 wraps)
	definition: ReportDefinitionV1;
	tableState: ReportTableState;
	onTableStateChange: (patch: Partial<ReportTableState>) => void;
	onToggleFavorite: (rowId: string) => void;
	fieldLabels?: Record<string, string>;
	/** The grain's catalog, which supplies every heading the static task map does not, and says
	 * which columns can be sorted. */
	taxonomy?: ReportTaxonomy | null;
	/** Per-column totals for the whole table, rendered as a sticky footer row. Keyed by field path,
	 * which is NOT the same set as the visible columns: row cells come from the wire nodes, so this
	 * can carry a duration path this report does not show. No footer renders unless a column the
	 * reader can see has an entry here - a report whose visible columns are all non-additive has
	 * nothing to total. */
	tableTotals?: Record<string, GroupTotal>;
}

const TableScroll = styled.div`
	width: 100%;
	max-height: 70vh;
	overflow: auto;
`;

/** `table-layout: fixed` is required, not cosmetic: only the visible row slice is mounted, so the
 * default `auto` layout would recompute column widths from whichever rows happen to be mounted and
 * the columns would visibly resize as you scroll — drifting out of alignment with the sticky header,
 * whose `<th>` widths come from `header.getSize()`. Fixed layout takes the widths from the header
 * row alone, so they stay stable while scrolling and a column resize moves the body cells with it.
 * `width: max-content` (floored at the container via `min-width`) keeps those header widths exact
 * rather than letting the browser scale them to fit; wider-than-container tables scroll instead. */
const StyledTable = styled.table`
	width: max-content;
	min-width: 100%;
	table-layout: fixed;
	border-collapse: collapse;
	${tokens.font.body['2']}

	thead th {
		position: sticky;
		top: 0;
		z-index: 1;
		background: ${tokens.color.background.primary['1']};
	}

	tfoot td {
		position: sticky;
		bottom: 0;
		z-index: 1;
		background: ${tokens.color.background.primary['1']};
		/* An inset shadow, not a border-top: with border-collapse collapse the table owns the
		   collapsed borders and a cell's own border is dropped while that cell is stuck, so the
		   separator would vanish exactly when the footer is floating over the rows it totals —
		   the one moment it has to be there. HeaderRow sidesteps the same problem by putting its
		   border on the non-sticky tr; a tfoot row has no non-sticky element to hang one on. */
		box-shadow: inset 0 1px 0 ${tokens.color.border.enabled};
	}
`;

const HeaderRow = styled.tr`
	border-bottom: 1px solid ${tokens.color.border.enabled};
`;

const BodyRow = styled.tr<{$contextOnly: boolean}>`
	border-bottom: 1px solid ${tokens.color.border.decorative};
	height: 60px;
	color: ${({$contextOnly}) => ($contextOnly ? tokens.color.text.disabled.onlight : tokens.color.text.content.main)};
	font-style: ${({$contextOnly}) => ($contextOnly ? 'italic' : 'normal')};
`;

/** Every body cell is a fixed 60px tall with NO vertical padding, and centres its content.
 *
 * Vertical padding is what broke this before. `height` on a <tr> is a MINIMUM in table layout, so
 * 18px + content + 18px only lands on 60px when the content happens to be ~24px tall — true for a
 * line of text, false for any cell holding a control. The expander column renders a design-system
 * Button (any expandable row: a group header, or a task with subtasks), and the row grew to fit it.
 *
 * Setting the height here instead is height-agnostic: the cell is 60px for any content that fits
 * inside 60px, whatever the design system decides a Button, Pill or Link should measure. Tuning the
 * padding to one particular control's height would break the next time any of them changes. */
const BodyCell = styled.td`
	height: 60px;
	padding: 0 ${tokens.spacing['400']};
	vertical-align: middle;
`;

const GroupLabel = styled.span`
	font-weight: 600;
`;

/** Clamps the group label + count to one line instead of wrapping (which would push the row taller
 * than its fixed height). Replicates `Text`'s default typography/color directly rather than
 * nesting inside a real `Text`: `Text` doesn't forward a ref, so there's no way to measure its
 * rendered box from outside — measuring is required to show a tooltip only when the label is
 * actually cut off (see `GroupLabelCell` below), not on every hover. */
/** The caption cell holds a label and, when that column has one, its own total.
 *
 * A flex row rather than inline siblings, because both children are block-level for reasons of
 * their own: the clamped label below is a `-webkit-box`, and the design system renders a
 * non-interactable tooltip trigger inside a `div`. Left inline they stacked, putting the figure on
 * a second line and out of line with every other total in the same row - and with the footer cell
 * for the same column, which is the disagreement sharing the cell was meant to remove.
 *
 * `min-width: 0` lets the clamped label shrink instead of forcing the figure out of the cell. */
const CaptionCell = styled.div`
	display: flex;
	align-items: center;
	gap: ${tokens.spacing['200']};
	min-width: 0;
`;

const ClampedGroupLabel = styled.span`
	display: -webkit-box;
	-webkit-line-clamp: 1;
	-webkit-box-orient: vertical;
	overflow: hidden;
	word-break: break-all;
	${tokens.font.body['1']}
	color: ${tokens.color.text.content.main};
`;

/** The withheld-total trigger. Muted rather than absent: it has to be visible enough to invite the
 * hover that carries the explanation, and quiet enough not to read as a value.
 *
 * `body['2']` is the token `StyledTable` already sets on the table, so the dash matches the
 * surrounding cells rather than introducing a third size. */
const WithheldTotal = styled.span`
	cursor: help;
	color: ${tokens.color.text.disabled.onlight};
	${tokens.font.body['2']}
`;

/** Group-header label + count, clamped to one line with a tooltip shown only once the label is
 * actually truncated. The tooltip's content is a plain string (not the same rich label+count
 * markup rendered in the row) so it doesn't reproduce the label's bold styling — a tooltip
 * mirroring the row's own visual emphasis isn't a meaningful signal, just visual noise.
 *
 * `ClampedGroupLabel` — the element the ref measures — must stay mounted at a stable position
 * regardless of `isTruncated`: if it were only rendered inside the truncated branch (wrapped in
 * `Tooltip` in one branch, bare in the other), React would see a different root element type
 * between renders and unmount+remount it the moment truncation is detected — destroying the very
 * node being measured. Only the inner content (plain vs. `Tooltip`-wrapped) varies; the clamped
 * wrapper itself never does. */
function GroupLabelCell({label, count}: {label: string; count: number}): JSX.Element {
	const [ref, isTruncated] = useIsTruncated();
	const labelAndCount = (
		<>
			<GroupLabel>{label}</GroupLabel> ({count})
		</>
	);
	return (
		<ClampedGroupLabel ref={ref}>
			{isTruncated ? (
				<Tooltip content={`${label} (${count})`} interactable={false}>
					<span>{labelAndCount}</span>
				</Tooltip>
			) : (
				labelAndCount
			)}
		</ClampedGroupLabel>
	);
}

/** Why a withheld total was refused, in the reader's words - the two designed tooltips, as a
 * subhead and its supporting text. Values are message ids, resolved via `formatMessage` in
 * `TotalCell` - the English wording lives in the default-messages catalogue, not here.
 *
 * A blank where a number belongs reads as zero, which is the one thing this feature must never let
 * a reader believe - so a refused total renders a hoverable dash carrying the reason, not an empty
 * cell, and not the "N/A" sentinel (which means a genuinely not-applicable single-row field, a
 * different case). A blank IS still correct for a column that never had a total - a percentage, a
 * name - and that case renders nothing, with nothing to hover.
 *
 * `scope` picks the wording only: the table variants swap the opening clause, since a whole-table
 * footer is not a group.
 *
 * `group` is keyed over a narrower reason set than `table`: a group header's own total is only ever
 * withheld for something its OWN rows disagree on (see `WithheldReason` in `useClientGrouping.ts`),
 * so `truncatedRows` - a whole-fetch fact - can never reach one. Excluding it from `group`'s key set
 * is what lets the compiler hold that boundary; inventing a "this group is truncated" sentence that
 * can never render would hide the same guarantee instead. `TotalCell` reads the `table` entry for
 * that reason whatever the scope, so if the boundary ever broke the reader still gets a truthful
 * sentence rather than a render-time throw. */
const WITHHELD_MESSAGE_IDS: {
	group: Record<Exclude<WithheldReason, 'truncatedRows'>, {titleId: string; detailsId: string}>;
	table: Record<WithheldReason, {titleId: string; detailsId: string}>;
} = {
	group: {
		mixedBudgetTypes: {
			titleId: 'ai_report_builder.total_withheld.group.mixed_budget_types.title',
			detailsId: 'ai_report_builder.total_withheld.group.mixed_budget_types.details',
		},
		mixedUnits: {
			titleId: 'ai_report_builder.total_withheld.group.mixed_units.title',
			detailsId: 'ai_report_builder.total_withheld.group.mixed_units.details',
		},
	},
	table: {
		mixedBudgetTypes: {
			titleId: 'ai_report_builder.total_withheld.table.mixed_budget_types.title',
			detailsId: 'ai_report_builder.total_withheld.table.mixed_budget_types.details',
		},
		mixedUnits: {
			titleId: 'ai_report_builder.total_withheld.table.mixed_units.title',
			detailsId: 'ai_report_builder.total_withheld.table.mixed_units.details',
		},
		truncatedRows: {
			titleId: 'ai_report_builder.total_withheld.table.truncated_rows.title',
			detailsId: 'ai_report_builder.total_withheld.table.truncated_rows.details',
		},
	},
};

/** One column's total: a figure, a refusal the reader can read, or nothing.
 *
 * `Tooltip.Item` is the design system's own subhead-over-body layout, so the subhead/body font
 * tokens come from it rather than from anything declared here. `width` is left at its default `m`.
 *
 * The explanation is also the trigger's accessible name. A tooltip alone is invisible to a screen
 * reader, and the explanation IS the feature - a reader who cannot reach it is back to reading a
 * blank as a zero.
 *
 * Rendered as a JSX element at both call sites (never invoked as a plain function), so calling
 * `useIntl` here is a component's hook, not a bare function reaching for one - the hook is called
 * before either early return so it runs on every render regardless of `total`. */
function TotalCell({total, scope}: {total: GroupTotal | undefined; scope: 'group' | 'table'}): JSX.Element | null {
	const {formatMessage} = useIntl();
	if (total === undefined) {
		return null;
	}
	if (total.kind === 'total') {
		return <Text>{formatDuration(total.minutes, total.unit)}</Text>;
	}
	// A group header's `groupTotals` is never built with `truncatedRows` (see `WithheldReason`), so
	// the second branch's narrowing holds by construction. It is expressed as a condition rather
	// than as an assertion that throws: this is render code, and a throw here reaches the app error
	// boundary and replaces the whole builder with a crash screen. If the invariant ever broke, the
	// report-scoped wording is still a truthful explanation of a truncated fetch.
	const {titleId, detailsId} =
		scope === 'table' || total.reason === 'truncatedRows'
			? WITHHELD_MESSAGE_IDS.table[total.reason]
			: WITHHELD_MESSAGE_IDS.group[total.reason];
	const title = formatMessage({id: titleId});
	const details = formatMessage({id: detailsId});
	return (
		<Tooltip content={<Tooltip.Item title={title} details={details} />} interactable={false}>
			<WithheldTotal aria-label={`${title}. ${details}`} role="note">
				&mdash;
			</WithheldTotal>
		</Tooltip>
	);
}

/** Narrows a row to a `GroupHeaderRow` (see `useClientGrouping`) so its label/count/totals can be
 * rendered instead of per-column data cells. */
function isGroupHeaderRow(row: ReportRow): row is GroupHeaderRow {
	return (row as GroupHeaderRow).isGroupHeader === true;
}

/** Indent added per nesting level, so a nested group's chevron sits directly under its parent
 * group's label.
 *
 * This is the expander column's width as the design draws it: every cell in that column is 56 wide,
 * and each nesting level in the design's table starts one such cell further right (0, 56, 112). It is
 * table geometry, the same category as the 60px row height above, and NOT a measurement of the
 * design-system Button the cell happens to contain -- deriving it from that control's intrinsic size
 * is exactly what `BodyCell` above refuses to do, because the design system is free to change it. */
export const INDENT_STEP_PX = 56;

/** How many indent steps a row gets. A group header indents once per grouping level above it. A data
 * row inside a group does NOT count its own group level -- a leaf's text sits flush with the label of
 * the group it belongs to, not one step past it. Nesting below a data row (subtasks, deliverables)
 * still adds a step, so an ungrouped table indents subrows under their parent exactly as before.
 *
 * `row.depth` alone cannot express this: it counts the row's own level too, which is right for a
 * group header and one step too far for a leaf. Exported for direct testing: the value is applied
 * through a `calc()` padding, which jsdom drops, so it cannot be read back off the rendered DOM. */
export function indentLevel(row: Row<ReportRow>): number {
	let groupAncestors = 0;
	let dataAncestors = 0;
	for (const parent of row.getParentRows()) {
		if (isGroupHeaderRow(parent.original)) {
			groupAncestors++;
		} else {
			dataAncestors++;
		}
	}
	if (isGroupHeaderRow(row.original)) {
		return groupAncestors;
	}
	return Math.max(groupAncestors - 1, 0) + dataAncestors;
}

/** Pure helper for `onDragEnd`: reorders `order` by moving `activeId` to `overId`'s position via
 * `arrayMove`. Returns `order` unchanged (same reference) if either id is a fixed column
 * (`__`-prefixed, e.g. `__expander`/`__favorite`) or either id is not present in `order` — fixed
 * columns are never draggable/droppable targets, and unknown ids indicate a stale drag event. */
export function computeReorderedColumns(order: string[], activeId: string, overId: string): string[] {
	if (activeId === overId) {
		return order;
	}
	if (FIXED_COLUMN_IDS.includes(activeId) || FIXED_COLUMN_IDS.includes(overId)) {
		return order;
	}

	const activeIndex = order.indexOf(activeId);
	const overIndex = order.indexOf(overId);
	if (activeIndex === -1 || overIndex === -1) {
		return order;
	}

	return arrayMove(order, activeIndex, overIndex);
}

export function ReportTable({
	rows,
	definition,
	tableState,
	onTableStateChange,
	onToggleFavorite,
	fieldLabels,
	taxonomy,
	tableTotals,
}: ReportTableProps): JSX.Element {
	const {formatMessage} = useIntl();
	const columns = useReportTableColumns(definition, {fieldLabels, taxonomy, onToggleFavorite});

	const table = useReactTable({
		data: rows,
		columns,
		state: {
			sorting: tableState.sorting,
			columnOrder: tableState.columnOrder,
			columnSizing: tableState.columnSizing,
			columnVisibility: tableState.columnVisibility,
		},
		initialState: {
			expanded: true,
		},
		columnResizeMode: 'onChange',
		getCoreRowModel: getCoreRowModel(),
		getExpandedRowModel: getExpandedRowModel(),
		getSortedRowModel: getSortedRowModel(),
		getSubRows: row => row.children,
		onSortingChange: updater => {
			onTableStateChange({sorting: functionalUpdate(updater, tableState.sorting)});
		},
		onColumnOrderChange: updater => {
			onTableStateChange({columnOrder: functionalUpdate(updater, tableState.columnOrder)});
		},
		onColumnSizingChange: updater => {
			onTableStateChange({columnSizing: functionalUpdate(updater, tableState.columnSizing)});
		},
		onColumnVisibilityChange: updater => {
			onTableStateChange({columnVisibility: functionalUpdate(updater, tableState.columnVisibility)});
		},
	});

	// Current column order restricted to draggable data columns (fixed columns are excluded from
	// the SortableContext — they are never draggable or valid drop targets).
	const dataColumnOrder = table
		.getAllLeafColumns()
		.map(column => column.id)
		.filter(id => !FIXED_COLUMN_IDS.includes(id));

	// A distance activation constraint is required so a plain click on a header still fires its
	// sort `onClick`; without it the default PointerSensor starts a zero-distance drag on pointerdown
	// and swallows the click, so column sorting never triggers. A real drag (>8px) still reorders.
	const sensors = useSensors(
		useSensor(PointerSensor, {activationConstraint: {distance: 8}}),
		useSensor(KeyboardSensor, {coordinateGetter: sortableKeyboardCoordinates})
	);

	const handleDragEnd = (event: DragEndEvent): void => {
		const activeId = event.active.id;
		const overId = event.over?.id;
		if (overId == null) {
			return;
		}

		const currentOrder = tableState.columnOrder.length > 0 ? tableState.columnOrder : dataColumnOrder;
		const nextOrder = computeReorderedColumns(currentOrder, String(activeId), String(overId));
		if (nextOrder !== currentOrder) {
			onTableStateChange({columnOrder: nextOrder});
		}
	};

	const scrollRef = useRef<HTMLDivElement>(null);
	const rowModel = table.getRowModel().rows;
	const rowVirtualizer = useVirtualizer({
		count: rowModel.length,
		getScrollElement: () => scrollRef.current,
		estimateSize: () => 60,
		overscan: 12,
	});
	const virtualItems = rowVirtualizer.getVirtualItems();
	const totalSize = rowVirtualizer.getTotalSize();
	const paddingTop = virtualItems.length > 0 ? virtualItems[0].start : 0;
	const paddingBottom = virtualItems.length > 0 ? totalSize - virtualItems[virtualItems.length - 1].end : 0;
	const visibleLeafColumns = table.getVisibleLeafColumns();
	// The spacer rows that stand in for the unmounted slice above/below the window must span every
	// visible column — a single-cell spacer row is a one-column row the layout has to reconcile
	// against the real rows.
	const spacerColSpan = visibleLeafColumns.length;
	// The footer's "Total" caption goes in the first visible column that is not the expander or the
	// favourite — the slot a group header puts its label in at nesting level zero. Resolved by
	// identity rather than by a fixed index: hiding the leading data column shifts that index, and a
	// report showing no data column at all would land it on the favourite column, which renders an
	// empty cell and would drop the caption entirely.
	const captionColumnId = visibleLeafColumns.find(column => !FIXED_COLUMN_IDS.includes(column.id))?.id;
	// A footer is only worth rendering when a column the reader can actually SEE has a total. Row
	// cells are built from the wire nodes rather than from the definition, so `tableTotals`
	// legitimately carries duration paths that are not columns of this report at all — and hiding a
	// duration column leaves its total in the record too. Keying the footer off the record alone
	// therefore rendered a row captioned `Total` with no figure anywhere in it.
	const hasVisibleTotal =
		tableTotals !== undefined && visibleLeafColumns.some(column => tableTotals[column.id] !== undefined);

	const renderRow = (row: (typeof rowModel)[number], flatIndex: number): JSX.Element => {
		if (isGroupHeaderRow(row.original)) {
			const groupRow = row.original;
			return (
				<BodyRow
					key={row.id}
					ref={rowVirtualizer.measureElement}
					data-index={flatIndex}
					data-cy="group-row"
					$contextOnly={false}
					data-context-only={false}
				>
					{row.getVisibleCells().map(cell => {
						const columnId = cell.column.id;

						// Expander/favourite columns are not meaningful for a synthetic
						// group-header row (it isn't a real task) — render nothing, but keep the
						// expander's chevron so groups stay collapsible.
						if (columnId === EXPANDER_COLUMN_ID) {
							return (
								<BodyCell key={cell.id}>
									{row.getCanExpand() && (
										<Button
											iconOnly
											emphasis="low"
											icon={row.getIsExpanded() ? 'chevronUp' : 'chevronDown'}
											onPress={row.getToggleExpandedHandler()}
											aria-label="Toggle row expansion"
										/>
									)}
								</BodyCell>
							);
						}
						if (columnId === FAVORITE_COLUMN_ID) {
							return <BodyCell key={cell.id} />;
						}

						// The label sits in the same column the footer captions, resolved by identity for
						// the same reason, and it SHARES that cell with the column's own total rather than
						// replacing it. Hiding the leading data column, or dragging a duration column to the
						// front, otherwise dropped that column's figure from every group header while the
						// footer kept it - two rows of one table disagreeing about whether a column can be
						// totalled at all, which is the absent-versus-withheld confusion this whole
						// treatment exists to remove. Indented so nested grouping levels visually nest; see
						// `indentLevel`.
						if (columnId === captionColumnId) {
							return (
								<BodyCell
									key={cell.id}
									style={{
										paddingLeft: `calc(${tokens.spacing['300']} + ${indentLevel(row) * INDENT_STEP_PX}px)`,
									}}
								>
									<CaptionCell>
										<GroupLabelCell label={groupRow.groupLabel} count={groupRow.groupCount} />
										<TotalCell total={groupRow.groupTotals[columnId]} scope="group" />
									</CaptionCell>
								</BodyCell>
							);
						}

						return (
							<BodyCell key={cell.id}>
								<TotalCell total={groupRow.groupTotals[columnId]} scope="group" />
							</BodyCell>
						);
					})}
				</BodyRow>
			);
		}

		return (
			<BodyRow
				key={row.id}
				ref={rowVirtualizer.measureElement}
				data-index={flatIndex}
				data-cy="data-row"
				$contextOnly={!row.original.matchedFilter}
				data-context-only={!row.original.matchedFilter}
			>
				{row.getVisibleCells().map((cell, cellIndex) => {
					// Indent the first cell after the expander column so nested rows visually nest
					// under their parent regardless of column order/visibility; see `indentLevel`.
					const isFirstDataCell = cellIndex === 1 && cell.column.id !== EXPANDER_COLUMN_ID;
					return (
						<BodyCell
							key={cell.id}
							style={
								isFirstDataCell
									? {paddingLeft: `calc(${tokens.spacing['300']} + ${indentLevel(row) * INDENT_STEP_PX}px)`}
									: undefined
							}
						>
							{flexRender(cell.column.columnDef.cell, cell.getContext())}
						</BodyCell>
					);
				})}
			</BodyRow>
		);
	};

	return (
		<TableScroll ref={scrollRef}>
			<DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
				<StyledTable>
					<thead>
						{table.getHeaderGroups().map(headerGroup => (
							<HeaderRow key={headerGroup.id}>
								<SortableContext items={dataColumnOrder} strategy={horizontalListSortingStrategy}>
									{headerGroup.headers.map(header => (
										<DraggableHeader key={header.id} header={header} />
									))}
								</SortableContext>
							</HeaderRow>
						))}
					</thead>
					<tbody>
						{paddingTop > 0 && (
							<tr aria-hidden={true}>
								<td colSpan={spacerColSpan} style={{height: paddingTop, padding: 0, border: 0}} />
							</tr>
						)}
						{virtualItems.map(virtualRow => renderRow(rowModel[virtualRow.index], virtualRow.index))}
						{paddingBottom > 0 && (
							<tr aria-hidden={true}>
								<td colSpan={spacerColSpan} style={{height: paddingBottom, padding: 0, border: 0}} />
							</tr>
						)}
					</tbody>
					{hasVisibleTotal && tableTotals !== undefined && (
						<tfoot>
							<BodyRow $contextOnly={false} data-cy="table-total-row" data-testid="table-total-row">
								{visibleLeafColumns.map(column => {
									if (column.id === EXPANDER_COLUMN_ID || column.id === FAVORITE_COLUMN_ID) {
										return <BodyCell key={column.id} />;
									}
									// The caption shares its cell with that column's own total rather than
									// replacing it. Which column it lands in is not fixed: hiding the leading
									// column, or dragging a duration column to the front, puts an additive
									// column in this slot, and rendering only the caption would drop that
									// column's figure from the row that claims to total every column.
									if (column.id === captionColumnId) {
										return (
											<BodyCell key={column.id}>
												<CaptionCell>
													<GroupLabel>
														{formatMessage({id: 'ai_report_builder.table_total_label'})}
													</GroupLabel>
													<TotalCell total={tableTotals[column.id]} scope="table" />
												</CaptionCell>
											</BodyCell>
										);
									}
									return (
										<BodyCell key={column.id}>
											<TotalCell total={tableTotals[column.id]} scope="table" />
										</BodyCell>
									);
								})}
							</BodyRow>
						</tfoot>
					)}
				</StyledTable>
			</DndContext>
		</TableScroll>
	);
}
